import type { ConnectedConnectorResponse, ConnectionType } from '../types/connection.types';

/** Onboarding flows that share Step 2 / Step 3 components. The flow is the
 *  only signal we keep about which platform the user is configuring — it
 *  survives a hard refresh because it lives in the URL. Exported so
 *  `resolveCatalogSource` callers can type the discriminant without re-deriving
 *  it from the platform context. */
export type OnboardingPlatform = 'google' | 'meta';

export interface OnboardingPlatformContext {
  platform: OnboardingPlatform;
  /** True if the current path is in the Meta integration tree. Convenience
   *  alias for `platform === 'meta'` to keep call sites readable. */
  isMetaFlow: boolean;
  /** Backend connector code matching this platform's catalog source. */
  connectorCode: ConnectionType;
  /** Human-readable label rendered in shared progress copy. */
  sourceLabel: string;
  /** Step 1 hub path (Connect Platforms) for this flow. */
  hubPath: '/onboarding/integration/google' | '/onboarding/integration/meta';
  /** Step 2 (Product Overview) path for this flow. */
  productOverviewPath:
    | '/onboarding/product-overview'
    | '/onboarding/integration/meta/product-overview';
  /** Step 3 (Complete Import) path for this flow — also used as the retry
   *  redirect target so the user stays on Step 3 after a fresh sync. */
  completeImportPath:
    | '/onboarding/complete-import'
    | '/onboarding/integration/meta/complete-import';
}

const META_CONTEXT: OnboardingPlatformContext = {
  platform: 'meta',
  isMetaFlow: true,
  connectorCode: 'META_COMMERCE',
  sourceLabel: 'Meta Commerce Manager',
  hubPath: '/onboarding/integration/meta',
  productOverviewPath: '/onboarding/integration/meta/product-overview',
  completeImportPath: '/onboarding/integration/meta/complete-import',
};

const GOOGLE_CONTEXT: OnboardingPlatformContext = {
  platform: 'google',
  isMetaFlow: false,
  connectorCode: 'GOOGLE_MERCHANT',
  sourceLabel: 'Google Merchant Center',
  hubPath: '/onboarding/integration/google',
  productOverviewPath: '/onboarding/product-overview',
  completeImportPath: '/onboarding/complete-import',
};

/**
 * Maps a router pathname to the platform-specific context the shared
 * Step 2 / Step 3 / Import-Catalog screens need (connector code, hub path,
 * source label). Returns `null` for paths that don't match either tree.
 *
 * **Production code should prefer `requirePlatformContext` below.** The
 * shared screens are statically routed under known paths, so a null result
 * is a programmer/invariant violation, not a runtime-recoverable state.
 * This raw `derivePlatformContext` exists primarily so tests (and any
 * future caller that legitimately wants to *probe* without throwing) can
 * see the null without an exception.
 *
 * The regexes require either `/` or end-of-string immediately after the
 * platform segment so paths like `/onboarding/integration/meta-search-test`
 * or `…/google-meta-hybrid` don't false-match via a loose `startsWith`.
 */
const META_PATH = /^\/onboarding\/integration\/meta(\/|$)/;
const GOOGLE_INTEGRATION_PATH = /^\/onboarding\/integration\/google(\/|$)/;

export function derivePlatformContext(pathname: string): OnboardingPlatformContext | null {
  if (META_PATH.test(pathname)) return META_CONTEXT;
  if (
    GOOGLE_INTEGRATION_PATH.test(pathname) ||
    pathname === '/onboarding/product-overview' ||
    pathname === '/onboarding/complete-import'
  ) {
    return GOOGLE_CONTEXT;
  }
  return null;
}

/**
 * Invariant-checking variant for screens that are statically routed under a
 * known platform tree (Step 2 / Step 3 / Import-Catalog). Throws if the
 * pathname doesn't match either tree — caught by the route error boundary
 * rather than silently rendering a Google-shaped fallback (which is the
 * regression every previous "fix" of this code path quietly re-introduced
 * by spreading a default-Google object at the call site).
 *
 * Production code should call this; tests can call `derivePlatformContext`
 * directly to assert the null branch.
 */
export function requirePlatformContext(pathname: string): OnboardingPlatformContext {
  const ctx = derivePlatformContext(pathname);
  if (!ctx) {
    throw new Error(
      `requirePlatformContext: no onboarding platform matches pathname "${pathname}". ` +
        'Routes that mount Step 2 / Step 3 / Import-Catalog screens must live under ' +
        '/onboarding/integration/{google,meta} or the bare /onboarding/{product-overview,complete-import} paths.',
    );
  }
  return ctx;
}

// ---------------------------------------------------------------------------
// Catalog-source resolution
// ---------------------------------------------------------------------------

/** What kind of catalog source the user has wired up. `connection` carries a
 *  backend row (GMC, Shopify, Meta); `import-file` is the local-only file
 *  upload variant of the Google flow; `none` means no source is set yet. */
export type CatalogSourceKind = 'connection' | 'import-file' | 'none';

/** Resolved catalog source for the active onboarding flow. The Step 2 /
 *  Step 3 screens consume this directly so they don't rely on a per-platform
 *  hardcoded connector code (which was the GMC-only assumption the previous
 *  context shape carried). */
export interface CatalogSourceInfo {
  kind: CatalogSourceKind;
  /** Backend connector code for the active source. Undefined for `import-file`
   *  (no backend row) and `none`. */
  connectorCode: ConnectionType | undefined;
  /** Connection id for the active source. Undefined for `import-file` and
   *  `none`. Required to call `/connections/:id/metrics`, `/sync/products/list`,
   *  and `POST /sync/products` for that connection. */
  connectionId: string | undefined;
  /** Human-readable label rendered in Step 2 / Step 3 copy. Always populated
   *  so callers can interpolate without fallbacks. */
  sourceLabel: string;
}

const PLATFORM_DEFAULT_LABEL: Record<OnboardingPlatform, string> = {
  google: 'Google Merchant Center',
  meta: 'Meta Commerce Manager',
};

/** Human-readable catalog-source labels per connector, shared with the global
 *  catalog-import progress widget so its subtitle matches the Step-3 copy. */
export const CONNECTOR_LABEL: Partial<Record<ConnectionType, string>> = {
  GOOGLE_MERCHANT: 'Google Merchant Center',
  META_COMMERCE: 'Meta Commerce Manager',
  SHOPIFY: 'Shopify',
  MANUAL_UPLOAD: 'Imported Catalog File',
};

/**
 * Resolve which catalog source the user is using for the current flow.
 *
 * Google flow: priority order is GMC → Shopify → Import File → none. Falls
 * through to whichever is present so the wizard adapts to mixed connection
 * setups without hardcoding GMC.
 *
 * Meta flow: only META_COMMERCE is a valid catalog source today, so the
 * function returns the Meta connection (or `none`) regardless of the other
 * arguments.
 *
 * Pure function — easy to unit test against connected-connector fixtures.
 */
export function resolveCatalogSource(args: {
  platform: OnboardingPlatform;
  connectedConnectors: ConnectedConnectorResponse[] | undefined;
  hasImportFile: boolean;
}): CatalogSourceInfo {
  const { platform, connectedConnectors, hasImportFile } = args;

  const findByCode = (code: ConnectionType) => connectedConnectors?.find((c) => c.code === code);

  if (platform === 'meta') {
    const conn = findByCode('META_COMMERCE');
    if (conn) {
      return {
        kind: 'connection',
        connectorCode: 'META_COMMERCE',
        connectionId: conn.connectionId,
        sourceLabel: CONNECTOR_LABEL.META_COMMERCE ?? PLATFORM_DEFAULT_LABEL.meta,
      };
    }
    return noneInfo(platform);
  }

  // Google flow: GMC takes precedence so existing GMC users see no change.
  const gmc = findByCode('GOOGLE_MERCHANT');
  if (gmc) {
    return {
      kind: 'connection',
      connectorCode: 'GOOGLE_MERCHANT',
      connectionId: gmc.connectionId,
      sourceLabel: CONNECTOR_LABEL.GOOGLE_MERCHANT ?? PLATFORM_DEFAULT_LABEL.google,
    };
  }

  const shopify = findByCode('SHOPIFY');
  if (shopify) {
    return {
      kind: 'connection',
      connectorCode: 'SHOPIFY',
      connectionId: shopify.connectionId,
      sourceLabel: CONNECTOR_LABEL.SHOPIFY ?? 'Shopify',
    };
  }

  if (hasImportFile) {
    return {
      kind: 'import-file',
      connectorCode: undefined,
      connectionId: undefined,
      sourceLabel: 'Imported Catalog File',
    };
  }

  return noneInfo(platform);
}

function noneInfo(platform: OnboardingPlatform): CatalogSourceInfo {
  return {
    kind: 'none',
    connectorCode: undefined,
    connectionId: undefined,
    sourceLabel: PLATFORM_DEFAULT_LABEL[platform],
  };
}
